import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useEffect, useRef } from "react";
import { Animated, Easing, Platform, Pressable, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { LiveWaveform } from "./LiveWaveform";

import { colors, fonts } from "@/lib/theme";

const STRIPE_WIDTH = 340;
const USE_NATIVE_DRIVER = Platform.OS !== "web";

function formatTime(secs: number): string {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
}

interface Props {
    seconds: number;
    meter: number;
    reduced: boolean;
    onCancel: () => void;
    onStop: () => void;
}

/**
 * 340-wide pill: cancel × | red dot | live waveform | timer | red stop □.
 * Pure presentation; receives `seconds`/`meter` from props, fires `onCancel`/
 * `onStop` callbacks. Owns its enter-animation Animated.Values.
 */
export function RecordingStripe({ seconds, meter, reduced, onCancel, onStop }: Props) {
    const scale = useRef(new Animated.Value(reduced ? 1 : 0.6)).current;
    const opacity = useRef(new Animated.Value(reduced ? 1 : 0)).current;

    useEffect(() => {
        if (reduced) {
            scale.setValue(1);
            opacity.setValue(1);
            return;
        }
        Animated.parallel([
            Animated.spring(scale, {
                toValue: 1,
                friction: 9,
                tension: 80,
                useNativeDriver: USE_NATIVE_DRIVER
            }),
            Animated.timing(opacity, {
                toValue: 1,
                duration: 200,
                easing: Easing.out(Easing.ease),
                useNativeDriver: USE_NATIVE_DRIVER
            })
        ]).start();
    }, [scale, opacity, reduced]);

    return (
        <Animated.View
            pointerEvents="auto"
            style={[styles.stripe, { transform: [{ scale }], opacity }]}
            accessibilityRole="toolbar"
            accessibilityLabel="Recording controls"
        >
            <Pressable
                onPress={onCancel}
                accessibilityRole="button"
                accessibilityLabel="Cancel recording"
                android_ripple={{ color: "rgba(0,0,0,0.12)", borderless: true }}
                style={({ pressed }) => [styles.cancelBtn, pressed && styles.btnPressed]}
            >
                <MaterialCommunityIcons name="close" size={18} color={colors.gray600} />
            </Pressable>

            <View style={styles.body}>
                <View style={styles.recDot} />
                <View style={styles.waveWrap}>
                    <LiveWaveform meter={meter} />
                </View>
                <Text style={styles.timer}>{formatTime(seconds)}</Text>
            </View>

            <Pressable
                onPress={onStop}
                accessibilityRole="button"
                accessibilityLabel="Stop and transcribe"
                android_ripple={{ color: "rgba(255,255,255,0.25)", borderless: true }}
                style={({ pressed }) => [styles.stopBtn, pressed && styles.btnPressed]}
            >
                <View style={styles.stopIcon} />
            </Pressable>
        </Animated.View>
    );
}

const styles = StyleSheet.create({
    stripe: {
        width: STRIPE_WIDTH,
        maxWidth: "100%",
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingVertical: 8,
        paddingLeft: 6,
        paddingRight: 8,
        borderRadius: 999,
        backgroundColor: "#fff",
        borderWidth: 1,
        borderColor: colors.surfaceBorder,
        ...Platform.select({
            ios: {
                shadowColor: "#1F1547",
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: 0.18,
                shadowRadius: 14
            },
            android: { elevation: 10 },
            default: {}
        })
    },
    body: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingHorizontal: 4,
        minWidth: 0
    },
    cancelBtn: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: colors.gray100,
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0
    },
    stopBtn: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: "#EF4444",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        ...Platform.select({
            ios: {
                shadowColor: "#EF4444",
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.35,
                shadowRadius: 14
            },
            android: { elevation: 8 },
            default: {}
        })
    },
    btnPressed: { opacity: 0.7 },
    stopIcon: {
        width: 14,
        height: 14,
        borderRadius: 3,
        backgroundColor: "#fff"
    },
    recDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: "#EF4444",
        flexShrink: 0
    },
    waveWrap: {
        flex: 1,
        minWidth: 0,
        height: 28,
        justifyContent: "center"
    },
    timer: {
        fontSize: 13,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary,
        letterSpacing: -0.3,
        flexShrink: 0
    }
});
